import React, { useEffect, useState } from "react";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useNavigate } from "react-router-dom";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getAdminActions } from "../../../config/actions";
import Pagination from "../../common/Pagination";
import { newsReportRequest } from "./newsReportApi";
import "../DistributorsAdd/DistributorsAdd.css";
import "../DistributorDashboard/DistributorDashboard.css";
import "../HelpContent/HelpContent.css";
import "./NewsReport.css";

const PAGE_SIZE = 10;

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const htmlToEditorState = (html) => {
  if (!html) return EditorState.createEmpty();
  const { contentBlocks, entityMap } = htmlToDraft(html);
  return EditorState.createWithContent(
    ContentState.createFromBlockArray(contentBlocks, entityMap)
  );
};

const NewsReport = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("user_type");
  const userId = localStorage.getItem("user_id");

  if (userType !== "7") {
    navigate("/");
    return null;
  }

  const actions = getAdminActions(userId);
  const [expandedMenu, setExpandedMenu] = useState(null);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await newsReportRequest("/news-reports");
      setReports(data.reports || []);
    } catch (err) {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const totalPages = Math.max(1, Math.ceil(reports.length / PAGE_SIZE));
  const paginatedReports = reports.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openForm = (report = null) => {
    setEditing(report);
    setTitle(report ? report.title : "");
    setEditorState(htmlToEditorState(report ? report.description : ""));
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!title.trim() || !editorState.getCurrentContent().hasText()) {
      setError("Title and description are required.");
      return;
    }
    const description = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setSaving(true);
    setError("");
    try {
      await newsReportRequest(editing ? `/news-reports/${editing.id}` : "/news-reports", {
        method: editing ? "PUT" : "POST",
        body: { title, description },
      });
      setFormOpen(false);
      setSuccessMessage(editing ? "News report updated successfully." : "News report added successfully.");
      setEditing(null);
      setSuccessOpen(true);
      await fetchReports();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await newsReportRequest(`/news-reports/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      const remaining = reports.length - 1;
      setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(remaining / PAGE_SIZE))));
      setSuccessMessage("News report deleted successfully.");
      setSuccessOpen(true);
      await fetchReports();
    } catch (err) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="distributors-add-container">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={actions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />
        <div className="distributors-add-content">
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <h3>News Reports</h3>
            </div>
            <div className="add-btn">
              <button onClick={() => openForm()}>Add News Report</button>
            </div>
          </div>

          <div className="distributors-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="loader-spinner"></div>
                <p>Loading news reports...</p>
              </div>
            ) : reports.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-newspaper"></i>
                <p>No news reports yet</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="distributors-table help-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th className="help-col-actions">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReports.map((report) => (
                        <tr key={report.id}>
                          <td>{report.title}</td>
                          <td>{stripHtml(report.description).slice(0, 120)}</td>
                          <td>
                            <div className="help-actions-cell">
                              <button
                                className="action-btn"
                                title="Edit"
                                onClick={() => openForm(report)}
                              >
                                <i className="fa fa-pencil-square-o"></i>
                              </button>
                              <button
                                className="action-btn cancel-btn"
                                title="Delete"
                                onClick={() => setDeleteTarget(report)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={formOpen} toggle={closeForm} centered size="lg">
        <ModalHeader className="modal-header-custom" toggle={closeForm}>
          {editing ? "Edit News Report" : "Add News Report"}
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="help-form-group">
            <label>Title*</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="help-form-group">
            <label>Description*</label>
            <div
              className="help-faq-editor"
              style={{ border: "1px solid rgb(77, 0, 77)", borderRadius: "4px" }}
            >
              <Editor
                editorState={editorState}
                onEditorStateChange={setEditorState}
                wrapperClassName="help-faq-editor-wrapper"
                editorClassName="help-faq-editor-body"
                toolbarClassName="help-faq-editor-toolbar"
              />
            </div>
          </div>
          {error && <div className="help-error">{error}</div>}
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-secondary" onClick={closeForm} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader className="modal-header-custom" toggle={() => setDeleteTarget(null)}>
          Delete News Report
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          Delete "{deleteTarget?.title}"? This cannot be undone.
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button
            className="btn-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={successOpen} toggle={() => setSuccessOpen(false)} centered>
        <ModalHeader className="modal-header-custom" toggle={() => setSuccessOpen(false)}>
          Success
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="success-state news-success-state">
            <div className="success-icon news-success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Success!</h3>
            <p>{successMessage}</p>
          </div>
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-primary-modern" onClick={() => setSuccessOpen(false)}>
            OK
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NewsReport;
